import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useAdminApi } from '../contexts/AdminApiContext'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import './RoleProtection.css'

interface RoleProtectionProps {
  children: React.ReactNode
  requiredRole?: 'admin' | 'super_admin'
  fallbackMessage?: string
}

interface UserRole {
  id: string
  email: string
  name: string
  role: string
  roleAssignedAt: string
  isAdmin: boolean
  isSuperAdmin: boolean
}

const RoleProtection: React.FC<RoleProtectionProps> = ({ 
  children, 
  requiredRole = 'admin',
  fallbackMessage
}) => {
  const { user, isAuthenticated } = useAuth()
  const { apiService, isReady } = useAdminApi()
  const [roleStatus, setRoleStatus] = useState<{
    loading: boolean
    hasAccess: boolean
    userRole?: UserRole
    error?: string
  }>({ loading: true, hasAccess: false })

  useEffect(() => {
    if (!isAuthenticated || !isReady || !user) {
      setRoleStatus({ loading: false, hasAccess: false, error: 'Authentication required' })
      return
    }

    checkRoleAccess()
  }, [isAuthenticated, isReady, user])

  const checkRoleAccess = async () => {
    setRoleStatus(prev => ({ ...prev, loading: true }))

    try {
      const authStatus = await apiService.getAuthStatus()
      const userRole = authStatus.user as UserRole

      let hasAccess = false
      if (requiredRole === 'admin') {
        hasAccess = userRole.isAdmin
      } else if (requiredRole === 'super_admin') {
        hasAccess = userRole.isSuperAdmin
      }

      setRoleStatus({
        loading: false,
        hasAccess,
        userRole,
        error: hasAccess ? undefined : `${requiredRole.replace('_', ' ')} access required`
      })
    } catch (error) {
      console.error('Role check failed:', error)
      
      // For development/local environment, allow access if role system isn't set up
      const isDevelopment = window.location.hostname === 'localhost'
      if (isDevelopment && error instanceof Error && error.message.includes('Forbidden')) {
        console.warn('⚠️  Role system not configured - allowing access in development mode')
        setRoleStatus({
          loading: false,
          hasAccess: true,
          userRole: {
            id: user?.id || 'dev-user',
            email: user?.emailAddresses?.[0]?.emailAddress || 'dev@localhost',
            name: user?.fullName || user?.firstName || 'Developer',
            role: 'admin',
            roleAssignedAt: new Date().toISOString(),
            isAdmin: true,
            isSuperAdmin: true
          },
          error: undefined
        })
        return
      }
      
      setRoleStatus({
        loading: false,
        hasAccess: false,
        error: error instanceof Error ? error.message : 'Role verification failed'
      })
    }
  }

  if (roleStatus.loading) {
    return (
      <div className="role-protection-loading">
        <div className="loading-container glassmorphism">
          <RefreshCw className="loading-spinner" size={32} />
          <h2>Verifying Access</h2>
          <p>Checking your admin permissions...</p>
        </div>
      </div>
    )
  }

  if (!roleStatus.hasAccess) {
    return (
      <div className="role-protection-denied">
        <div className="access-denied-container glassmorphism">
          <AlertTriangle size={48} className="warning-icon" />
          <h2>Access Denied</h2>
          <p className="error-message">
            {fallbackMessage || roleStatus.error || 'You do not have permission to access this area.'}
          </p>
          
          {roleStatus.userRole && (
            <div className="user-role-info">
              <p className="user-info">
                Logged in as: <strong>{roleStatus.userRole.name || roleStatus.userRole.email}</strong>
              </p>
              <p className="role-info">
                Current role: <span className={`role-badge role-${roleStatus.userRole.role}`}>
                  {roleStatus.userRole.role}
                </span>
              </p>
            </div>
          )}

          <button 
            className="btn btn-secondary retry-btn" 
            onClick={checkRoleAccess}
          >
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Access granted - render children with role context
  return (
    <div className="role-protection-granted">
      {children}
    </div>
  )
}

export default RoleProtection